import { useId, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api, type Requirement } from '../lib/api.js';
import { Icon } from './Icon.js';

/** What a list of "the places this production needs" leaves out: decided-no and replaced rows. */
const NOT_NEEDED: ReadonlySet<string> = new Set(['DISMISSED', 'SUPERSEDED', 'ARCHIVED']);

export function neededRequirements(requirements: readonly Requirement[]): Requirement[] {
  return requirements.filter((r) => !NOT_NEEDED.has(r.status));
}

/**
 * NAMING THE PLACES BY HAND, on the page a production opens on.
 *
 * The same verb as the requirement dialog (`AddRequirementDialog`, `POST
 * …/requirements`): a title, approved at once with human provenance, and no
 * scene scope — on step 1 there is usually no screenplay to scope it to, and a
 * unit base belongs to the production anyway. Inline rather than in a modal,
 * because here it IS the page's job, and a list being built should stay in
 * sight while the next item is typed.
 *
 * The list is the production's own requirements, read from the same query the
 * workspace uses, so one added here is the one the Locations tab shows.
 */
export function ManualRequirementsPanel({
  projectId,
  requirements,
}: {
  projectId: string;
  requirements: readonly Requirement[];
}) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const id = useId();
  const trimmed = title.trim();

  const add = useMutation({
    mutationFn: (name: string) => api.addRequirement(projectId, { title: name, sceneIds: [] }),
    onSuccess: async () => {
      setTitle('');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['requirements', projectId] }),
        queryClient.invalidateQueries({ queryKey: ['review-progress', projectId] }),
        queryClient.invalidateQueries({ queryKey: ['matchable', projectId] }),
      ]);
      // Ready for the next one: a list is typed one place after another.
      inputRef.current?.focus();
    },
  });

  const needed = neededRequirements(requirements);

  return (
    <div className="manual-panel" data-testid="manual-requirements">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (trimmed !== '' && !add.isPending) add.mutate(trimmed);
        }}
      >
        <label className="flbl" htmlFor={`${id}-name`} style={{ display: 'block', marginBottom: 6 }}>
          A location the production needs
        </label>
        <div className="manual-add-row">
          <input
            ref={inputRef}
            id={`${id}-name`}
            className="intake-input"
            data-testid="add-requirement-name"
            value={title}
            maxLength={255}
            placeholder="e.g. Ferry deck, 1970s kitchen, rooftop bar"
            aria-describedby={add.isError ? `${id}-error` : undefined}
            onChange={(e) => {
              setTitle(e.target.value);
            }}
          />
          <button
            type="submit"
            className="btn btn-ghost"
            data-testid="add-requirement-submit"
            disabled={trimmed === '' || add.isPending}
          >
            {add.isPending ? 'Adding…' : 'Add'}
          </button>
        </div>
      </form>

      {add.isError ? (
        <p className="intake-error" id={`${id}-error`} role="alert" data-testid="add-requirement-error">
          {add.error.message || 'That requirement could not be added — try again in a moment.'}
        </p>
      ) : null}

      {needed.length === 0 ? (
        <p className="mm" style={{ margin: '14px 0 0', fontSize: 12.5, lineHeight: 1.6 }} data-testid="manual-requirements-empty">
          Nothing added yet. Add each place one at a time — they are marked as yours, not inferred
          by AI.
        </p>
      ) : (
        <ul className="manual-list" aria-label="Locations this production needs" data-testid="manual-requirement-list">
          {needed.map((r) => (
            <li key={r.id} data-testid={`manual-requirement-${r.id}`}>
              <Icon name="pin" size={14} />
              <span>{r.title}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
